import { Trans } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Card } from '@startuptools/ui/Card';
import { Checkbox } from '@startuptools/ui/Checkbox';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@startuptools/ui/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { Typography } from '@startuptools/ui/Typography';
import { Suspense, useMemo, useState } from 'react';
import {
  CompanyDirectorsDocument,
  CooptedMeetingParticipantsDocument,
  useCdSetInvitedMutation,
  useCmpCreateMutation,
  useCmpDeleteMutation,
  useCmpSetInvitedMutation,
  useCompanyDirectorsSuspenseQuery,
  useCooptedMeetingParticipantsSuspenseQuery,
} from '../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { BM, regularBoardMembers } from '../../common/types';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { IdentityEditableEmail } from '../../components/editable-email/IdentityEditableEmail';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { JuridicalKind } from '../../models/identity.model';
import { InputState } from '@startuptools/business-ui/identity/types';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';

const BoardMeetingMembersInner = () => {
  const companyId = useCompanyId();
  const [showAddIdentity, setShowAddIdentity] = useState(false);
  const {
    data: { companyDirectors },
  } = useCompanyDirectorsSuspenseQuery({ variables: { companyId } });
  const {
    data: { cooptedMeetingParticipants },
  } = useCooptedMeetingParticipantsSuspenseQuery({ variables: { companyId } });
  // eslint-disable-next-line sut/loading-mutation
  const [doCdSetInvited] = useCdSetInvitedMutation();
  // eslint-disable-next-line sut/loading-mutation
  const [doCmpSetInvited] = useCmpSetInvitedMutation();
  // eslint-disable-next-line sut/loading-mutation
  const [doCmpDelete] = useCmpDeleteMutation({ refetchQueries: [CooptedMeetingParticipantsDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doCmpCreate] = useCmpCreateMutation({ refetchQueries: [CooptedMeetingParticipantsDocument] });

  const ordinary = useMemo(
    () => companyDirectors.filter(cd => cd.functions.some(f => regularBoardMembers.includes(f.code))),
    [companyDirectors],
  );

  const deputies = useMemo(
    () => companyDirectors.filter(cd => cd.functions.some(f => f.code === BM.Su)),
    [companyDirectors],
  );

  return (
    <>
      <Stack gap={3}>
        <Typography level="h2">
          <Trans id="Board Meeting Participants" />
        </Typography>
        <Alert variant="outlined" color="primary" startDecorator={<InfoOutlinedIcon />}>
          <Trans id="Set who should be invited by default when board meetings are created" />
        </Alert>
        <Card>
          <Typography level="title-lg">
            <Trans id="Ordinary Board Members (Always Invited to Board Meetings by Default)" />
          </Typography>
          <Table sx={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th />
                <th>
                  <Trans id="Email" />
                </th>
              </tr>
            </thead>
            <tbody>
              {ordinary.map(cd => (
                <tr key={cd.id}>
                  <td>{cd.identity && <Identity identity={cd.identity} />}</td>
                  <td>
                    {cd.identity && (
                      <IdentityEditableEmail id={cd.identity.id} refetchQueries={[CompanyDirectorsDocument]}>
                        {cd.identity.email}
                      </IdentityEditableEmail>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        <Card>
          <Typography level="title-lg">
            <Trans id="Deputy Board Members" />
          </Typography>
          <Table sx={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th>
                  <Trans id="Invite" />
                </th>
                <th />
                <th>
                  <Trans id="Email" />
                </th>
              </tr>
            </thead>
            <tbody>
              {deputies.map(cd => (
                <tr key={cd.id}>
                  <td style={{ width: '70px' }}>
                    <Tooltip title={<Trans id="Set this to always invite this person" />}>
                      <Checkbox
                        checked={cd.invited}
                        onChange={() =>
                          doCdSetInvited({ variables: { companyId, input: { id: cd.id, invited: !cd.invited } } })
                        }
                      />
                    </Tooltip>
                  </td>
                  <td>{cd.identity && <Identity identity={cd.identity} />}</td>
                  <td>
                    {cd.identity && (
                      <IdentityEditableEmail id={cd.identity.id} refetchQueries={[CompanyDirectorsDocument]}>
                        {cd.identity.email}
                      </IdentityEditableEmail>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        <Card>
          <Typography level="title-lg">
            <Trans id="Coopted Board Members" />
          </Typography>
          <Box>
            <Button onClick={() => setShowAddIdentity(true)}>
              <Trans id="Add Coopted Meeting Participant" />
            </Button>
          </Box>
          {Boolean(cooptedMeetingParticipants.length) && (
            <Table sx={{ tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th>
                    <Trans id="Invite" />
                  </th>
                  <th />
                  <th>
                    <Trans id="Email" />
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {cooptedMeetingParticipants.map(cmp => (
                  <tr key={cmp.id}>
                    <td style={{ width: '70px' }}>
                      <Tooltip title={<Trans id="Set this to always invite this person" />}>
                        <Checkbox
                          checked={cmp.invited}
                          onChange={() =>
                            doCmpSetInvited({ variables: { companyId, input: { id: cmp.id, invited: !cmp.invited } } })
                          }
                        />
                      </Tooltip>
                    </td>
                    <td>{cmp.identity && <Identity identity={cmp.identity} />}</td>
                    <td>
                      {cmp.identity && (
                        <IdentityEditableEmail
                          id={cmp.identity.id}
                          refetchQueries={[CooptedMeetingParticipantsDocument]}
                        >
                          {cmp.identity.email}
                        </IdentityEditableEmail>
                      )}
                    </td>
                    <td>
                      <IconButton color="danger" onClick={() => doCmpDelete({ variables: { companyId, id: cmp.id } })}>
                        <DeleteIcon />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </Stack>
      {showAddIdentity && (
        <AddIdentity
          headline={<Trans id="Add Coopted Meeting Participant" />}
          juridicalKinds={[JuridicalKind.Person]}
          availableInputStates={{
            [InputState.System]: true,
            [InputState.CompanyBoard]: false,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          defaultInputState={InputState.Swedish}
          detailFieldsSettings={{}}
          handleCreate={async identity => {
            const identityInput = identityToInput(identity);
            return doCmpCreate({ variables: { companyId, input: { identityInput } } }).then(() =>
              setShowAddIdentity(false),
            );
          }}
          onClose={() => {
            setShowAddIdentity(false);
          }}
        />
      )}
    </>
  );
};

export const BoardMeetingMembers = () => {
  return (
    <Suspense>
      <BoardMeetingMembersInner />
    </Suspense>
  );
};
